'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { gstinError } from '../../utils/gstin';
import {
  Building2, Search, Plus, Edit2, Trash2, AlertCircle, X, Globe, MapPin, Mail, Phone, TrendingUp, Download, Upload, Filter, Crosshair,
  Briefcase, Activity, ChevronRight, ShieldCheck, ArrowUpRight,
  BarChart3, Zap, Clock, ExternalLink, ChevronDown, MoreVertical, ChevronLeft
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import ImportModal from '../../components/crm/ImportModal';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkActionBar from '../../components/crm/BulkActionBar';
import SavedViewsBar from '../../components/crm/SavedViewsBar';
import FilterBuilder from '../../components/crm/FilterBuilder';
import InlineEditSelect from '../../components/crm/InlineEditSelect';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import { downloadCsv } from '../../utils/exportCsv';
import { CountrySelect, StateSelect } from '../../components/CountryStateSelect';
import { SUPPORTED_CURRENCIES } from '../../constants/currencies';
import Tabs from '../../components/ui/Tabs';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
const ACCOUNT_STATUS_OPTIONS = [
  { value: 'active', label: 'ACTIVE' },
  { value: 'inactive', label: 'INACTIVE' },
  { value: 'prospect', label: 'PROSPECT' },
];

const ACCOUNT_FILTER_FIELDS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'industry', label: 'Industry' },
  { value: 'status', label: 'Status' },
  { value: 'city', label: 'City' },
  { value: 'country', label: 'Country' },
  { value: 'dealTotal', label: 'Deal Total' },
];

export default function AccountsPage() {
  const navigate = useNavigate();
  const baseCurrency = useCompanyCurrency();
  const bulk = useBulkSelection();
  const PAGE_SIZE = 20;
  const [accounts, setAccounts] = useState([]);
  const [accountsMeta, setAccountsMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', website: '', industry: '', city: '', state: '', country: '', gstin: '', defaultCurrency: '', parentAccountId: ''
  });
  const [showMenu, setShowMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [customersOnly, setCustomersOnly] = useState(false);
  const [advancedConditions, setAdvancedConditions] = useState([]);
  const [view, setView] = useState('all'); // 'all' | 'renewals'
  const [renewals, setRenewals] = useState([]);
  const [renewalsLoading, setRenewalsLoading] = useState(false);
  const [renewalsError, setRenewalsError] = useState(null);

  // Reset to page 1 whenever the search term changes so results aren't scoped to a stale page
  useEffect(() => {
    setPage(1);
  }, [searchQuery, customersOnly, advancedConditions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAccounts();
    }, searchQuery ? 350 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, customersOnly, advancedConditions]);

  const advancedFiltersParam = (() => {
    const valid = advancedConditions.filter(c => c.field && c.value !== '');
    return valid.length ? JSON.stringify(valid) : undefined;
  })();

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/crm/accounts', { params: { page, limit: PAGE_SIZE, search: searchQuery || undefined, isCustomer: customersOnly ? 'true' : undefined, advancedFilters: advancedFiltersParam } });
      const data = response.data;
      if (Array.isArray(data)) {
        setAccounts(data);
        setAccountsMeta({ total: data.length, page: 1, pages: 1 });
      } else {
        setAccounts(data.accounts || []);
        setAccountsMeta({
          total: data.total ?? data.accounts?.length ?? 0,
          page: data.page ?? 1,
          pages: data.pages ?? 1,
        });
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to sync accounts.';
      setError(msg);
      alertService.error('Error', msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchRenewals = async () => {
    try {
      setRenewalsLoading(true);
      setRenewalsError(null);
      const response = await api.get('/crm/accounts/renewals');
      setRenewals(response.data.renewals || []);
    } catch (err) {
      setRenewalsError(err.response?.data?.message || 'Failed to load renewals.');
    } finally {
      setRenewalsLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'renewals') fetchRenewals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleExportAccounts = async () => {
    setShowMenu(false);
    try {
      const response = await api.get('/crm/accounts/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accounts-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export accounts');
    }
  };

  // Search is now applied server-side (see fetchAccounts), so `accounts` is
  // already the current page's filtered result set.
  const filtered = accounts;
  const totalPages = accountsMeta.pages || 1;
  const totalAccounts = accountsMeta.total ?? accounts.length;

  const handleAdd = () => {
    setEditingId(null);
    setFormData({ name: '', email: '', phone: '', website: '', industry: '', city: '', state: '', country: '', gstin: '', defaultCurrency: '', parentAccountId: '' });
    setShowAddModal(true);
  };

  const handleEdit = (account) => {
    setEditingId(account._id);
    setFormData({ ...account, parentAccountId: account.parentAccountId?._id || account.parentAccountId || '' });
    setShowAddModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (gstinError(formData.gstin)) { alertService.error('GSTIN looks wrong', gstinError(formData.gstin)); return; }
    setSaveLoading(true);
    try {
      const payload = { ...formData, parentAccountId: formData.parentAccountId || null };
      if (editingId) {
        await api.put(`/crm/accounts/${editingId}`, payload);
        alertService.success('Updated', 'Account saved');
      } else {
        await api.post('/crm/accounts', payload);
        alertService.success('Success', 'Account created');
      }
      setShowAddModal(false);
      fetchAccounts();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to save');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (account) => {
    const confirmed = await alertService.confirm('Delete Account?', `Dissolve "${account.name}" and all records?`, 'danger', 'Delete');
    if (confirmed) {
      try {
        await api.delete(`/crm/accounts/${account._id}`);
        alertService.success('Deleted', 'Account archived');
        fetchAccounts();
      } catch (err) { alertService.error('Error', 'Action failed'); }
    }
  };

  const runBulkAction = async (action, value) => {
    try {
      const res = await api.post('/crm/accounts/bulk', { accountIds: bulk.selectedIds, action, value });
      const count = res.data?.modifiedCount ?? bulk.selectedCount;
      bulk.clear();
      fetchAccounts();
      alertService.success(action === 'delete' ? 'Deleted' : 'Updated', `${count} account(s) ${action === 'delete' ? 'deleted' : 'updated'}`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk action failed');
    }
  };

  const handleBulkStatusChange = (value) => runBulkAction('change_status', value);
  const handleBulkAssign = (value) => runBulkAction('assign', value);
  const handleBulkDelete = () => runBulkAction('delete');

  const handleBulkExport = () => {
    const selected = accounts.filter(a => bulk.selected.has(a._id));
    downloadCsv(
      'accounts-selected.csv',
      ['Name', 'Website', 'Email', 'Phone', 'Industry', 'Status'],
      selected.map(a => [a.name, a.website, a.email, a.phone, a.industry, a.status])
    );
  };

  const totalPortfolio = accounts.reduce((sum, a) => sum + (a.annualRevenue || 0), 0);

  if (loading && accounts.length === 0) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader
        title="Accounts"
        subtitle={<>{totalAccounts} Accounts &middot; Page {accountsMeta.page || 1} of {totalPages}</>}
        icon={Building2}
        actions={<>
<div className="relative flex flex-wrap items-center gap-2">
<button
            onClick={() => setShowMenu(v => !v)}
            title="More actions"
            aria-label="More actions"
            className="h-9 w-9 flex items-center justify-center bg-white border border-[#D8DDE6] rounded hover:bg-[#F3F2F2] transition-all"
          >
            <MoreVertical size={16} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-11 z-20 w-48 bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] overflow-hidden">
              <button
                onClick={handleExportAccounts}
                className="w-full text-left px-4 py-2.5 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] flex items-center gap-2"
              >
                <Upload size={14} /> Export CSV
              </button>
              <button
                onClick={() => { setShowMenu(false); setShowImportModal(true); }}
                className="w-full text-left px-4 py-2.5 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] flex items-center gap-2"
              >
                <Download size={14} /> Import
              </button>
            </div>
          )}
          <button onClick={handleAdd} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Plus size={14} /> New Account
          </button>
</div>
  </>}
      />

      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Accounts"
        importEndpoint="/crm/accounts/import"
        sampleHeaders={['Name', 'Website', 'Email', 'Phone', 'Industry', 'Status', 'Notes']}
        sampleRow={['Acme Corp', 'acme.com', 'contact@acme.com', '9998887777', 'Manufacturing', 'active', 'Key enterprise account']}
        onImported={() => fetchAccounts()}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total Portfolio</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{formatCurrency(totalPortfolio, baseCurrency, { compact: true })}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Active Accounts</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{accounts.length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Accounts With Active Deals</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">
            {accounts.length ? Math.round((accounts.filter(a => (a.deals || []).length > 0 || a.dealTotal > 0).length / accounts.length) * 100) : 0}%
          </h3>
        </div>
      </div>

      {/* View Tabs */}
      <Tabs tabs={[{ key: 'all', label: 'All Accounts' }, { key: 'renewals', label: 'Renewals' }]} value={view} onChange={setView} />

      {view === 'all' && (
      <>
      {/* Control Bar */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-3 flex flex-wrap items-center gap-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
          <input
            type="text"
            placeholder="Search by name, industry, website…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 bg-[#F3F2F2] px-3 h-9 rounded border border-[#D8DDE6] cursor-pointer">
            <input
              type="checkbox"
              checked={customersOnly}
              onChange={e => setCustomersOnly(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#0176D3] cursor-pointer"
            />
            <Filter size={12} className="text-[#706E6B]" />
            <span className="text-[12px] font-bold text-[#080707] uppercase">Customers Only</span>
          </label>

          <FilterBuilder
            fields={ACCOUNT_FILTER_FIELDS}
            conditions={advancedConditions}
            onChange={setAdvancedConditions}
          />

          <SavedViewsBar
            resourceType="account"
            currentFilters={{ search: searchQuery, customersOnly, advancedConditions }}
            onApply={(filters) => {
              setSearchQuery(filters.search || '');
              setCustomersOnly(!!filters.customersOnly);
              setAdvancedConditions(filters.advancedConditions || []);
            }}
          />
        </div>
      </div>

      <BulkActionBar
        selectedCount={bulk.selectedCount}
        onClear={bulk.clear}
        statusOptions={ACCOUNT_STATUS_OPTIONS}
        onStatusChange={handleBulkStatusChange}
        onAssign={handleBulkAssign}
        assignLabel="Owner"
        assignPlaceholder="Assign owner…"
        onDelete={handleBulkDelete}
        onExport={handleBulkExport}
        entityLabel="account"
      />

      {/* Accounts Table */}
      <div className="sf-table-container">
        <table className="sf-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={bulk.isAllSelected(filtered.map(a => a._id))}
                  onChange={() => bulk.toggleAll(filtered.map(a => a._id))}
                  aria-label="Select all accounts"
                  className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                />
              </th>
              <th>Account Name</th>
              <th>Industry Sector</th>
              <th>Global HQ</th>
              <th>Status Details</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? filtered.map(account => (
              <tr key={account._id} className="group">
                <td>
                  <input
                    type="checkbox"
                    checked={bulk.selected.has(account._id)}
                    onChange={() => bulk.toggle(account._id)}
                    aria-label={`Select ${account.name}`}
                    className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                  />
                </td>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-[#F3F2F2] border border-[#D8DDE6] flex items-center justify-center font-bold text-[#0176D3] text-[11px]">
                      {account.name?.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-[#0176D3] hover:underline cursor-pointer" onClick={() => navigate(`/dashboard/crm/accounts/${account._id}`)}>{account.name}</p>
                      <p className="text-[11px] text-[#706E6B] font-medium flex items-center gap-1"><Globe size={10} /> {account.website || 'No website'}</p>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="sf-badge sf-badge-neutral">{account.industry || 'No industry'}</span>
                    <InlineEditSelect
                      value={account.status || 'active'}
                      options={ACCOUNT_STATUS_OPTIONS}
                      badge={<span className={cn('sf-badge', account.status === 'inactive' ? 'sf-badge-error' : account.status === 'prospect' ? 'sf-badge-warning' : 'sf-badge-success')}>{(account.status || 'active').toUpperCase()}</span>}
                      onSave={async (newStatus) => {
                        await api.put(`/crm/accounts/${account._id}`, { status: newStatus });
                        fetchAccounts();
                      }}
                    />
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#706E6B]">
                    <MapPin size={12} className="text-[#BA0517]" /> {account.city || 'Anywhere'}{account.country ? `, ${account.country}` : ''}
                  </div>
                </td>
                <td>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-[#706E6B]">
                      <Mail size={10} className="text-[#0176D3]" /> {account.email || '—'}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-medium text-[#706E6B]">
                      <Phone size={10} className="text-[#2E844A]" /> {account.phone || '—'}
                    </div>
                  </div>
                </td>
                <td className="text-right">
                  <RowActionsMenu actions={[
                    { label: 'Refine Account', icon: Edit2, onClick: () => handleEdit(account) },
                    { label: 'Deep Inspection', icon: ExternalLink, onClick: () => navigate(`/dashboard/crm/accounts/${account._id}`) },
                    { label: 'Dissolve', icon: Trash2, danger: true, onClick: () => handleDelete(account) },
                  ]} />
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={6} className="py-24 text-center">
                  <Building2 className="mx-auto mb-4 opacity-10" size={48} />
                  <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">Zero global entity matches</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalAccounts > 0 && (
        <div className="flex items-center justify-between bg-white border border-[#E5E5E5] rounded-lg p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-[12px] font-medium text-[#706E6B]">
            Showing {((page - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(page * PAGE_SIZE, totalAccounts)} of {totalAccounts}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="h-8 px-3 flex items-center gap-1 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="text-[12px] font-bold text-[#080707] px-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="h-8 px-3 flex items-center gap-1 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {view === 'renewals' && (
        <div className="sf-table-container">
          {renewalsLoading ? (
            <PageLoader inline />
          ) : renewalsError ? (
            <div className="p-12 text-center text-[13px] font-bold text-[#BA0517]">{renewalsError}</div>
          ) : (
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Product</th>
                  <th>Next Renewal Date</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {renewals.length > 0 ? renewals.map(r => (
                  <tr key={r.accountId} className="group">
                    <td>
                      <p className="font-bold text-[#0176D3] hover:underline cursor-pointer" onClick={() => navigate(`/dashboard/crm/accounts/${r.accountId}`)}>{r.accountName}</p>
                    </td>
                    <td>
                      <span className="sf-badge sf-badge-neutral">{r.product || 'General'}</span>
                    </td>
                    <td>
                      <span className="text-[12px] font-medium text-[#706E6B]">
                        {r.nextInvoiceDate ? new Date(r.nextInvoiceDate).toLocaleDateString() : 'Not scheduled'}
                      </span>
                    </td>
                    <td className="text-right font-bold text-[#080707]">
                      {formatCurrency(r.monthlyAmount || 0, r.currency || baseCurrency)}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="py-24 text-center">
                      <Clock className="mx-auto mb-4 opacity-10" size={48} />
                      <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">No upcoming renewals</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-xl z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editingId ? 'Edit Account' : 'New Account'}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Account Name</label>
                  <input required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Industry Sector</label>
                  <input required value={formData.industry} onChange={e => setFormData(p => ({ ...p, industry: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Corporate Website</label>
                  <input value={formData.website} onChange={e => setFormData(p => ({ ...p, website: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Switchboard Phone</label>
                  <input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Management Email</label>
                <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Parent Account</label>
                <select
                  value={formData.parentAccountId || ''}
                  onChange={e => setFormData(p => ({ ...p, parentAccountId: e.target.value }))}
                  className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                >
                  <option value="">None (Top-Level Account)</option>
                  {accounts.filter(a => a._id !== editingId).map(a => (
                    <option key={a._id} value={a._id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Country</label>
                  <CountrySelect value={formData.country} onChange={country => setFormData(p => ({ ...p, country, state: '' }))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">State</label>
                  <StateSelect country={formData.country} value={formData.state} onChange={state => setFormData(p => ({ ...p, state }))} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">City</label>
                  <input value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">GSTIN</label>
                  <input value={formData.gstin || ''} onChange={e => setFormData(p => ({ ...p, gstin: e.target.value.toUpperCase() }))} placeholder="e.g. 29ABCDE1234F1ZW" className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  {formData.gstin && gstinError(formData.gstin) && <p className="text-[11px] text-[#BA0517]">{gstinError(formData.gstin)}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Default Currency</label>
                  <select value={formData.defaultCurrency || ''} onChange={e => setFormData(p => ({ ...p, defaultCurrency: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    <option value="">Use company default</option>
                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
